import axios from 'axios';
import { FilterType } from 'bindings/filters/FilterType';
import { FieldType } from 'bindings/forms/FieldType';
import { Cookies } from 'react-cookie';
import { atom, selector } from 'recoil';
import { URL_FOUNDATION } from '../config/keys';
import { AtomCategory, AtomState } from './utils/model/Model';
import { AVisibleCompanyFieldIds, visibleOf } from './additionalColumns';
import { GroupInsideType } from '../components_v2/Selector/actions';
import { ColumnDisplayOptions } from '../containers_v2/map/colorScale';

export type AdditionalFieldColumn = {
	field_id: number,
	field_name: string,
	field_type: FieldType,
	ishidded: boolean,
	// how the map shows the values
	display_options?: ColumnDisplayOptions,
}

export function isFieldTypeVisibleOnMap(type: FieldType): boolean {
	switch (type) {
		case 'Boolean':
		case 'Date':
		case 'Integer':
		case 'Number':
		case 'Select':
		case 'Multiselect':
		case 'User':
		case 'Money':
			return true;
	}
	return false;
}

export function isFieldTypeSortable(type: FieldType): boolean {
	switch (type) {
		case 'Text':
		case 'Number':
		case 'Date':
		case 'Boolean':
		case 'File':
		case 'Image':
		case 'Url':
		case 'Email':
		case 'Phone':
		case 'Address':
		case 'Location':
		case 'Integer':
		case 'TextArea':
		case 'Select':
		case 'Money':
			return true;
		case 'Form':
		case 'Company':
		case 'Dispatch':
		case 'User':
		case 'CheckIn':
		case 'Multiselect':
		case 'V3User':
		case 'Geography':
		case 'Signature':
		case 'Entity':
		case 'EntityList':
		case 'Period':
			return false;
	}

}

export function fieldTypeToFilterType(type: FieldType): FilterType {
	switch (type) {
		case 'Text': return 'string';
		case 'Number': return 'numeric';
		case 'Date': return 'temporal';
		case 'Boolean': return 'bool';
		case 'Select': return 'select';
		case 'Multiselect': return 'multi_select';
		case 'File': return 'nullable';
		case 'Image': return 'nullable';
		case 'Url': return 'string';
		case 'Email': return 'string';
		case 'Phone': return 'string';
		case 'Address': return 'string';
		case 'Location': return 'string';
		case 'User': return 'user';
		case 'Dispatch': return 'nullable';
		case 'Company': return 'nullable';
		case 'Integer': return 'numeric';
		case 'CheckIn': return 'nullable';
		case 'Form': return 'nullable';
		case 'TextArea': return 'string';
		case 'Money': return 'money';
	}
}

// -----------------------[ ATOM ]----------------------- //
const AGlobalAdditionalFieldColumns: AtomState<AdditionalFieldColumn[]> = {
	category: AtomCategory.GLOBAL,
	atom: atom({
		key: 'atom_global_additional_field_columns', // UNIQUE ID
		default: getAdditionalFieldColumns()
	})
};

export const AAdditionalFieldColumns = selector({
	key: 'AAdditionalFieldColumns',
	get: async({ get }) => get(AGlobalAdditionalFieldColumns.atom),
	set: ({ set }, newValue) => set(AGlobalAdditionalFieldColumns.atom, newValue),
});

/// The form fields this user may see on the company sheet: see
/// AVisibleAdditionalColumns for the two things that hide one.
export const AVisibleAdditionalFieldColumns = selector({
	key: 'AVisibleAdditionalFieldColumns',
	get: ({ get }) =>
		visibleOf(get(AVisibleCompanyFieldIds), GroupInsideType.FormField, get(AAdditionalFieldColumns), c => c.field_id, c => c.ishidded),
});

// -----------------------[ API ]------------------------ //
const cookies = new Cookies();
const token: string | null = cookies.get('id_token') || null;

axios.defaults.headers.common.Authorization = `${token ?? ''}`;

export async function getAdditionalFieldColumns(): Promise<AdditionalFieldColumn[]> {
	return axios.get(`${URL_FOUNDATION}/api/v2/additional-field-columns`).then(res => res.data);
}
